import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  Outlet, useLocation, useNavigate,
  useParams,
} from 'react-router-dom';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import Button from 'components/Button/Button';
import Panel from 'components/Panel/Panel';
import StepsList from 'components/StepsList/StepsList';
import AppRoutes from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';
import {
  CONST_DISABLED_STAKEHOLDERS,
  DEFAULT_STEP, MATERIAL_ASSESSMENT_STAKEHOLDERS_LIST, SECTION_TYPE,
} from 'constants/materialAssessment';
import { useGetMaterialityStakeholdersListStatusQuery } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import { useGetMaterialityStakeholdersQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { setMaterialId } from 'store/slices/materialAssessment/slice';

import styles from './scss/MaterialAssessmentImportLayout.module.scss';

function MaterialAssessmentImportLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { recordId = '', vcmId = 0 } = useParams();

  const {
    data: stakeholdersData,
  } = useGetMaterialityStakeholdersQuery({ maId: recordId, vcmId: Number(vcmId) }, {
    refetchOnMountOrArgChange: true,
    skip: vcmId === null,
  });

  const { data: sectionsDataVM } = useGetMaterialityStakeholdersListStatusQuery({
    maId: recordId,
    type: SECTION_TYPE.STAKEHOLDER_IDENTIFICATION_VCM,
    vcmId: Number(vcmId),
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (recordId) {
      dispatch(setMaterialId(Number(recordId)));
    }
  }, [recordId, dispatch]);

  const onDisabledStakeholders = () => {
    const disabledStakeholders = stakeholdersData?.data?.items?.filter((item) => item.isActive === false
    || CONST_DISABLED_STAKEHOLDERS.includes(item.type));
    return disabledStakeholders && disabledStakeholders?.length > 0
      ? disabledStakeholders.map((item) => item.type) : [];
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
  };

  const onStepChange = (stepType: string) => {
    navigate(`${AppRoutes.materialityAssessment}/${recordId}/${stepType}/${vcmId}`);
  };

  return (
    <div className={styles.root}>
      <Panel className={styles.panel}>
        <div className={styles.container}>
          <div className={styles.left}>
            <Button
              title={t('materialityAssessment.back')}
              onClick={goBack}
              variant={BUTTON_VARIANT.ghost}
              startIcon={<ArrowLeftIcon />}
            />
            <StepsList
              step={!Number(location.pathname.split('/')[3]) ? location.pathname.split('/')[3] : DEFAULT_STEP}
              stepsList={MATERIAL_ASSESSMENT_STAKEHOLDERS_LIST}
              handleStepChange={onStepChange}
              disableList={onDisabledStakeholders()}
              sectionItems={SECTION_TYPE.STAKEHOLDER_IDENTIFICATION}
              optionListVM={sectionsDataVM?.data?.subSections}
              recordId={recordId || ''}
              activeClick
              hideColor
            />
          </div>
          <div className={styles.right}>
            <Outlet />
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default MaterialAssessmentImportLayout;
